/**
 * @file LocalPickupSection.tsx
 *
 * The "Local pickup" section of the Shipping overview. Reads the pickup locations
 * (id-based, from the `/v1` shipping API) off the shipping store, builds the display
 * rows, and wires drag-reorder and delete (confirmed, via {@link useDeleteConfirm}) onto
 * the shared {@link OptionsListManager} card / {@link DraggableDataTable} — reorder
 * persists via the bulk `/v1` reorder endpoint, delete via the by-id delete. Edit is
 * routed by location id.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { notifyError } from '../feedback/toast';
import { useShippingStore } from '../stores/shipping.store';
import { useDeleteConfirm } from '../hooks/use-delete-confirm';
import { ConfirmModal } from '../components/ConfirmModal';
import { OptionsListManager } from './OptionsListManager';
import { buildPickupRows, type PickupRow } from './shipping-list';

/**
 * Renders the local-pickup-locations section.
 *
 * @returns The section card.
 */
export function LocalPickupSection() {
  const intl = useIntl();
  const navigate = useNavigate();

  const pickups = useShippingStore((s) => s.pickupLocations);
  const deletePickup = useShippingStore((s) => s.deletePickup);
  const reorderPickups = useShippingStore((s) => s.reorderPickups);

  const del = useDeleteConfirm<PickupRow>(
    (row) => deletePickup(row.id),
    intl.formatMessage(adminMessages.shippingDeletePickupError),
    {
      success: intl.formatMessage(adminMessages.toastDeleted),
      error: intl.formatMessage(adminMessages.toastError),
    },
  );

  const rows = useMemo(
    () => buildPickupRows(pickups, intl.formatMessage(adminMessages.shippingPickupUnnamed)),
    [pickups, intl],
  );

  const handleReorder = (next: PickupRow[]): void => {
    void reorderPickups(next.map((row) => row.id)).then((ok) => {
      if (!ok) {
        notifyError(intl.formatMessage(adminMessages.toastError));
      }
    });
  };

  return (
    <>
      <OptionsListManager<PickupRow>
        testId="shipping-pickup"
        tileName="shop"
        title={intl.formatMessage(adminMessages.shippingPickupTitle)}
        description={intl.formatMessage(adminMessages.shippingPickupDescription)}
        addButtonLabel={intl.formatMessage(adminMessages.shippingAddOption)}
        dragHandleAriaLabel={intl.formatMessage(adminMessages.shippingDragToReorder)}
        headerColumns={[
          { name: intl.formatMessage(adminMessages.shippingFieldName) },
          {
            address: {
              label: intl.formatMessage(adminMessages.shippingFieldPickupAddress),
              className: 'gv-col-truncate',
            },
          },
        ]}
        rows={rows}
        actions={[
          {
            icon: 'edit',
            label: intl.formatMessage(adminMessages.shippingEdit),
            testId: 'shipping-pickup-edit',
            onAction: (row) => navigate(`/shipping/local-pickup/${row.id}/edit`),
          },
          {
            icon: 'delete',
            label: intl.formatMessage(adminMessages.shippingDelete),
            testId: 'shipping-pickup-delete',
            onAction: (row) => del.ask(row),
          },
        ]}
        onAdd={() => navigate('/shipping/local-pickup/new')}
        onReorder={handleReorder}
        getPreviewLabel={(row) => row.name}
      />

      {del.target && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.shippingDeletePickupTitle)}
          message={intl.formatMessage(adminMessages.shippingDeletePickupMessage, {
            name: del.target.name,
          })}
          confirmLabel={intl.formatMessage(adminMessages.shippingDelete)}
          cancelLabel={intl.formatMessage(adminMessages.shippingCancel)}
          loadingLabel={intl.formatMessage(adminMessages.shippingDeleteLoading)}
          confirmVariant="destructive"
          loading={del.deleting}
          errorMessage={del.error ?? undefined}
          onPrimaryClick={del.confirm}
          onSecondaryClick={del.cancel}
          onClose={del.cancel}
        />
      )}
    </>
  );
}
